import React, { useState, useEffect, useRef } from 'react';
import PinField from 'react-pin-field';
import { FormattedMessage } from 'react-intl';
import LoginContainer from './LoginContainer';
import './PINLogin.scss';

const PIN_LENGTH = 6;

const PINInput = ({ onSubmit, setError, imageSrc, isError, secondaryCta }) => {
    const [showHelpMessage, setShowHelpMessage] = useState(false);
    const [pin, setPin] = useState('');
    const pinFieldRef = useRef(null);

    // Auto-submit when full PIN is entered
    useEffect(() => {
        if (pin.length === PIN_LENGTH) {
            onSubmit(pin);
        } else {
            setError(false);
        }
    }, [pin]);

    // Save initial scroll position to prevent unwanted scrolling on load
    const initialScrollPositionRef = useRef(null);

    useEffect(() => {
        // Save the scroll position when component first mounts
        if (initialScrollPositionRef.current === null) {
            initialScrollPositionRef.current = {
                x: window.scrollX,
                y: window.scrollY,
            };
        }
    }, []);

    // Prevent screen scrolling when inputs are focused/changed (but not on initial load)
    useEffect(() => {
        let isFirstFocus = true;

        const preventScroll = e => {
            // Only handle input focus events
            if (e.target && e.target.tagName === 'INPUT' && e.target.type === 'tel') {
                // On first focus (initial load), don't restore scroll - let it stay at initial position
                if (isFirstFocus) {
                    // Save the current scroll position as the new baseline after initial load
                    isFirstFocus = false;
                    // Don't prevent scroll on initial load - just track it
                    return;
                }

                // After initial load, prevent scroll changes
                const scrollY = window.scrollY;
                const scrollX = window.scrollX;

                // Restore scroll position immediately after focus
                requestAnimationFrame(() => {
                    window.scrollTo(scrollX, scrollY);
                });

                // Also restore after a small delay to catch iOS Safari's auto-scroll
                setTimeout(() => {
                    window.scrollTo(scrollX, scrollY);
                }, 50);
            }
        };

        // Listen for focus events on inputs
        document.addEventListener('focusin', preventScroll);

        return () => {
            document.removeEventListener('focusin', preventScroll);
        };
    }, []);

    // Ensure numeric keypad always opens on mobile
    useEffect(() => {
        let hasFocused = false;

        const enforceNumeric = () => {
            const inputs = pinFieldRef.current?.querySelectorAll('input');
            if (!inputs || inputs.length === 0) return;

            inputs.forEach(input => {
                input.type = 'tel';
                input.setAttribute('inputmode', 'numeric');
                input.setAttribute('pattern', '[0-9]*');
                input.autocomplete = 'one-time-code';
            });

            // Focus the first input once to trigger numeric keyboard on mobile
            if (!hasFocused) {
                const firstInput = inputs[0];
                if (firstInput && firstInput.isConnected && document.activeElement !== firstInput) {
                    firstInput.focus();
                    hasFocused = true;
                }
            }
        };

        // Apply immediately
        enforceNumeric();

        // react-pin-field re-renders inputs → observe and focus when inputs appear
        const observer = new MutationObserver(() => {
            enforceNumeric();
        });

        if (pinFieldRef.current) {
            observer.observe(pinFieldRef.current, { childList: true, subtree: true });
            // Try once more after observer is set up in case inputs appear immediately
            enforceNumeric();
        }

        return () => observer.disconnect();
    }, []);

    return (
        <LoginContainer className="login-form" imageSrc={imageSrc}>
            <p className="input-cta">
                <FormattedMessage
                    id="components.Login.PINLogin.enterPIN"
                    defaultMessage="Please enter the service PIN"
                />
            </p>

            {secondaryCta && <p className="input-cta-secondary">{secondaryCta}</p>}

            <div className="input-container">
                <div className="input">
                    <div className="six-digit-input" ref={pinFieldRef}>
                        <PinField
                            length={PIN_LENGTH}
                            autoFocus
                            value={pin}
                            onChange={setPin}
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            validate={/^[0-9]$/}
                        />
                    </div>

                    <div className="help-button-container">
                        <p className={`pin-error${isError && pin.length === PIN_LENGTH ? '' : ' pin-error-hidden'}`}>
                            <FormattedMessage
                                id="features.Login.PINLogin.invalidPIN"
                                defaultMessage="Invalid PIN, please try again"
                            />
                        </p>

                        <button type="button" className="help-button" onClick={() => setShowHelpMessage(prev => !prev)}>
                            <FormattedMessage
                                id="features.Login.PINLogin.helpPIN"
                                defaultMessage="Do not have the PIN?"
                            />
                        </button>

                        {showHelpMessage && (
                            <p className="help-button-message">
                                <FormattedMessage
                                    id="components.Login.PINLogin.contactInformant"
                                    defaultMessage="Please contact the family informant to get the PIN"
                                />
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </LoginContainer>
    );
};

export default PINInput;
